/**
 * The images filed into a map, as pictures rather than as URIs.
 *
 * The bytes are streamed through the authenticated API and held as object
 * URLs, the way the constellation's snapshots are: the board's images live in
 * a private bucket, and a link the browser could follow on its own would be a
 * link that outlives the session that asked for it.
 */

import { useCallback, useEffect, useState } from "react";

import { SystemicMapAPI } from "../infrastructure/api/SystemicMap.api";

export interface FiledImage {
    /** Where it sits in the map's own list — what the API addresses it by. */
    index: number;
    /** An object URL, alive only while this hook is mounted. */
    url: string;
}

export function useMapSnapshots(mapId: string | undefined, count: number) {
    const [images, setImages] = useState<FiledImage[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!mapId || count <= 0) {
            setImages([]);
            return;
        }

        let cancelled = false;
        const created: string[] = [];
        setLoading(true);

        (async () => {
            const loaded = await Promise.all(
                Array.from({ length: count }, (_, index) =>
                    SystemicMapAPI.getSnapshot(mapId, index)
                        .then((response) => ({
                            index,
                            url: URL.createObjectURL(response.data),
                        }))
                        // One image that will not load is one missing tile,
                        // not an empty gallery.
                        .catch(() => null),
                ),
            );

            if (cancelled) {
                loaded.forEach((image) => image && URL.revokeObjectURL(image.url));
                return;
            }

            const kept = loaded.filter((image): image is FiledImage => image !== null);
            kept.forEach((image) => created.push(image.url));
            setImages(kept);
            setLoading(false);
        })();

        return () => {
            cancelled = true;
            created.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [mapId, count]);

    const remove = useCallback(
        async (index: number) => {
            if (!mapId) return;
            await SystemicMapAPI.deleteSnapshot(mapId, index);
        },
        [mapId],
    );

    return { images, loading, remove };
}

/** Hand a file to the person looking at it. */
export const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Given back on the next tick: revoking it while the click is still being
    // handled cancels the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 0);
};
